import React, { useEffect, useState, useMemo } from 'react';
import { useAppContext } from '../context/AppContext';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  Container,
  Row,
  Col,
  Card,
  ListGroup,
  Button,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { useNavigate, useLocation } from 'react-router-dom';
import appConstants from '../constants/appConstants';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

const getAuthToken = () => localStorage.getItem('authToken');

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const Dashboard = () => {
  const { selectedAcademicYear } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(true);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    total_active_students: 0,
    total_collected: 0,
    total_pending: 0,
    recent_transactions: [],
  });
  const [feeChartData, setFeeChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Fees Collected',
        data: [],
        backgroundColor: '#007bff',
      },
    ],
  });
  const [attendanceChartData, setAttendanceChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Attendance on',
        data: [],
        backgroundColor: '#007bff',
      },
    ],
  });

  useEffect(() => {
    fetchDashboardData();

    const interval = setInterval(fetchDashboardData, 10000); // Poll every 10 seconds
    return () => clearInterval(interval); // Cleanup on unmount
    // eslint-disable-next-line
  }, [selectedAcademicYear]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (location.pathname === '/dashboard') {
        // Prevent navigating back (to login)
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location]);

  const test = async () => {
    try {
      const response = await axios.post(
        `https://ilmcampus.in/portal/phpscripts/get-accounts-balance.php`,
        {
          start_date: '2024-04-01',
          end_date: '2025-03-31',
        }, // JSON payload
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        },
      );
    } catch (e) {}
  };

  const fetchCashBankBalances = async () => {
    const token = getAuthToken();

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get_cash_bank_balances`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      console.log(response.data);

      setCashAccounts(response.data.cash_accounts);
      setBankAccounts(response.data.bank_accounts);
    } catch (error) {
      console.error('Error fetching cash/bank balances:', error);
    }
  };

  const fetchDashboardData = async () => {
    fetchCashBankBalances();

    // Only set loading on first load to prevent flickering
    if (firstLoaded) {
      setLoading(true);
    }

    const token = getAuthToken();
    if (!token) {
      //alert("Unauthorized! Please log in.");
      navigate('/login');
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      console.error('No academic year selected');
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-dashboard-data`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );

      if (response.data.status === 'success') {
        setFirstLoaded(false);
        setAttendanceChartData(response.data.data.attendance_chart);
        setFeeChartData(response.data.data.fees_chart);
        setDashboardData(response.data.data);
      } else {
        if (
          response.data.message === 'Expired token' ||
          response.data.message === 'Invalid or expired token'
        ) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          navigate('/login');
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      // Only clear loading if it was set (first load)
      if (firstLoaded) {
        setLoading(false);
      }
    }
  };

  // Memoize the display values to prevent flickering
  const totalStudents = useMemo(() => {
    return firstLoaded && loading ? '-' : dashboardData.total_active_students;
  }, [firstLoaded, loading, dashboardData.total_active_students]);

  const totalCollected = useMemo(() => {
    return firstLoaded && loading
      ? '-'
      : `₹${(dashboardData.total_collected / 1000).toFixed(0)}K`;
  }, [firstLoaded, loading, dashboardData.total_collected]);

  const totalPending = useMemo(() => {
    return firstLoaded && loading
      ? '-'
      : `₹${(dashboardData.total_pending / 1000).toFixed(0)}K`;
  }, [firstLoaded, loading, dashboardData.total_pending]);

  const totalCollectedFull = useMemo(() => {
    return dashboardData.total_collected.toLocaleString('en-IN');
  }, [dashboardData.total_collected]);

  const totalPendingFull = useMemo(() => {
    return dashboardData.total_pending.toLocaleString('en-IN');
  }, [dashboardData.total_pending]);

  return (
    <Layout>
      <Container fluid className="mt-4 px-4">
        {/* Top welcome header removed per request */}

        {/* Summary Cards */}
        <Row className="mb-4">
          <Col lg={4} md={6} className="mb-4">
            <Card
              className="h-100"
              style={{
                border: 'none',
                borderRadius: '15px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.25)',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = 'translateY(-5px)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = 'translateY(0)')
              }
            >
              <Card.Body className="p-3" style={{ minHeight: '130px' }}>
                <div className="d-flex align-items-center mb-2">
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                    }}
                  >
                    <i className="fas fa-users"></i>
                  </div>
                  <div className="ms-3">
                    <h6
                      style={{
                        marginBottom: '5px',
                        opacity: 0.9,
                        fontSize: '0.85rem',
                      }}
                    >
                      Total Students
                    </h6>
                    <h2 style={{ marginBottom: 0, fontWeight: 'bold' }}>
                      {totalStudents}
                    </h2>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '0.85rem',
                    opacity: 0.85,
                    background: 'rgba(255, 255, 255, 0.15)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                  }}
                >
                  Applications: {dashboardData.total_application}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} md={6} className="mb-4">
            <Card
              className="h-100"
              style={{
                border: 'none',
                borderRadius: '15px',
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                boxShadow: '0 8px 20px rgba(17, 153, 142, 0.25)',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = 'translateY(-5px)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = 'translateY(0)')
              }
            >
              <Card.Body className="p-3" style={{ minHeight: '130px' }}>
                <div className="d-flex align-items-center mb-2">
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                    }}
                  >
                    <i className="fas fa-rupee-sign"></i>
                  </div>
                  <div className="ms-3">
                    <h6
                      style={{
                        marginBottom: '5px',
                        opacity: 0.9,
                        fontSize: '0.85rem',
                      }}
                    >
                      Fees Collected
                    </h6>
                    <h2 style={{ marginBottom: 0, fontWeight: 'bold' }}>
                      {totalCollected}
                    </h2>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '0.85rem',
                    opacity: 0.85,
                    background: 'rgba(255, 255, 255, 0.15)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                  }}
                >
                  ₹{totalCollectedFull}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} md={6} className="mb-4">
            <Card
              className="h-100"
              style={{
                border: 'none',
                borderRadius: '15px',
                background: 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
                color: 'white',
                boxShadow: '0 8px 20px rgba(238, 9, 121, 0.25)',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = 'translateY(-5px)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = 'translateY(0)')
              }
            >
              <Card.Body className="p-3" style={{ minHeight: '130px' }}>
                <div className="d-flex align-items-center mb-2">
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                    }}
                  >
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <div className="ms-3">
                    <h6
                      style={{
                        marginBottom: '5px',
                        opacity: 0.9,
                        fontSize: '0.85rem',
                      }}
                    >
                      Pending Fees
                    </h6>
                    <h2 style={{ marginBottom: 0, fontWeight: 'bold' }}>
                      {totalPending}
                    </h2>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '0.85rem',
                    opacity: 0.85,
                    background: 'rgba(255, 255, 255, 0.15)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                  }}
                >
                  ₹{totalPendingFull}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Charts & Cash/Bank Section */}
        <Row>
          <Col lg={5} className="mb-4">
            <Card
              style={{
                border: 'none',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                height: '100%',
              }}
            >
              <Card.Body className="p-4">
                <h5
                  className="mb-4"
                  style={{ fontWeight: '600', color: '#333' }}
                >
                  <i
                    className="fas fa-chart-line me-2"
                    style={{ color: '#667eea' }}
                  ></i>
                  Fee Collection Trend
                </h5>
                <Bar
                  data={feeChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: { beginAtZero: true },
                    },
                  }}
                />
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} className="mb-4">
            <Card
              style={{
                border: 'none',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                height: '100%',
              }}
            >
              <Card.Body className="p-4">
                <h5
                  className="mb-4"
                  style={{ fontWeight: '600', color: '#333' }}
                >
                  <i
                    className="fas fa-chart-bar me-2"
                    style={{ color: '#11998e' }}
                  ></i>
                  Class Attendance
                </h5>
                <Bar
                  data={attendanceChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: { beginAtZero: true },
                    },
                  }}
                />
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} className="mb-4">
            {(cashAccounts.length > 0 || bankAccounts.length > 0) && (
              <Card
                style={{
                  border: 'none',
                  borderRadius: '15px',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                  height: '100%',
                }}
              >
                <Card.Body className="p-4">
                  <h5
                    className="mb-4"
                    style={{ fontWeight: '600', color: '#333' }}
                  >
                    <i
                      className="fas fa-wallet me-2"
                      style={{ color: '#ee0979' }}
                    ></i>
                    Accounts Balance
                  </h5>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {cashAccounts.map((item, index) => (
                      <div
                        key={`cash-${index}`}
                        style={{
                          padding: '12px',
                          marginBottom: '10px',
                          background: '#f8f9fa',
                          borderRadius: '10px',
                          borderLeft: '4px solid #667eea',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.85rem',
                            color: '#666',
                            marginBottom: '4px',
                          }}
                        >
                          {item.name}
                        </div>
                        <div
                          style={{
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            color: '#333',
                          }}
                        >
                          ₹{parseFloat(item.balance).toLocaleString('en-IN')}
                        </div>
                      </div>
                    ))}
                    {bankAccounts.map((item, index) => (
                      <div
                        key={`bank-${index}`}
                        style={{
                          padding: '12px',
                          marginBottom: '10px',
                          background: '#f8f9fa',
                          borderRadius: '10px',
                          borderLeft: '4px solid #11998e',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.85rem',
                            color: '#666',
                            marginBottom: '4px',
                          }}
                        >
                          {item.name}
                        </div>
                        <div
                          style={{
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            color: '#333',
                          }}
                        >
                          ₹{parseFloat(item.balance).toLocaleString('en-IN')}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default Dashboard;
